import { type InfiniteData, useMutation } from "@tanstack/react-query";

import { useMemo } from "react";

import { queryClient } from "@/config/react-query";

import { useUser } from "@/contexts/user-provider";

import { isLocalNoteId, useSelectedNote } from "@/hooks/use-selected-note";

import { clinicClock } from "@/lib/clinic-clock";
import { parseError, parseErrorCode } from "@/lib/parse-error";
import {
  PHOTO_DELETE_PERMISSION_DENIED_TOAST,
  PHOTO_PERMISSION_DENIED_TOAST,
  isPermissionDeniedError,
} from "@/lib/photo-upload-errors";
import { toMs } from "@/lib/timestamp";
import { toast } from "@/lib/use-toast";

import { Annotation } from "@/types/annotation";
import type { DocumentSummary } from "@/types/document";
import type { EventDetail, EventNoteRef, EventTiny } from "@/types/event";
import type { DetachedChartDoses } from "@/types/medication";
import type {
  AttachmentDayPage,
  NoteAttachment,
  NoteAttachmentTag,
  NoteBulkSignOffRequest,
  NoteBulkSignOffResult,
  NoteCreate,
  NoteDetail,
  NoteListItem,
  NoteScribeResponse,
  NoteSummary,
  NoteTemplateTableItem,
} from "@/types/notes";
import type { Page } from "@/types/pagination";
import type { PatientChart, PatientDetail } from "@/types/patient";
import type { PatientSidebarCounts } from "@/types/patient-sidebar";

import { diffAnnotations } from "./annotation-diff";
import { invalidatePrefilledAnswers } from "./note-cache-utils";
import {
  linkDocumentsToNote,
  unlinkDocumentFromNote,
  uploadNoteDocument,
} from "./note-documents";
import { noteQueryKeys } from "./note-query";
import {
  type NoteCompletionResult,
  addNoteAttachment,
  addNoteAttachmentFromUrl,
  applyAttachmentAnnotationsDiff,
  archiveNote,
  assignNoteAttachmentTags,
  bulkAssignNoteAttachmentTags,
  bulkFinalizeAttachmentUpload,
  bulkInitiateAttachmentUpload,
  bulkSignOffNotes,
  changeAttachmentPatient,
  chatWithAiNote,
  combineAudioFiles,
  createAiNote,
  createNote,
  createNoteAttachmentTag,
  deleteNoteAttachment,
  deleteNoteAttachmentTag,
  duplicateNoteAttachment,
  linkAttachmentsToNote,
  requestNoteSignOff,
  restoreAttachmentAnnotations,
  setNoteCompletion,
  signOffNote,
  startRecordingSession,
  togglePinNote,
  updateNoteAttachmentName,
  updateNoteAttachmentTag,
  updateNoteEventLink,
  updateNoteTemplateId,
  updateNoteTemplatePatientView,
  updateNoteTitle,
  uploadToGcs,
} from "./notes";
import { requestData } from "@/api/api-config";
import { todaysItemsQueryKeys } from "@/api/provider/billing/todays-items-query";
import { updatePatientInCache } from "@/api/provider/user/patient/patient-cache-utils";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
import {
  optimisticAdjustSidebarCounts,
  revertSidebarCounts,
} from "@/api/provider/user/patient/sidebar-cache-utils";
import { eventQueryKeys } from "@/query/check-in/calendar/event-query";
import { stockQueryKeys } from "@/query/inventory/stock";
import { medicationsQueryKeys } from "@/query/medications/medications";

// Re-export the diff helper so external callers can import it from the same
// module as the mutation hook if they prefer; the standalone module is what
// unit tests use to avoid pulling in React Query.
export { diffAnnotations };

interface EventNoteIdsPatch {
  removeNoteIds: Iterable<string>;
  addEventId?: string;
  addNoteId?: string;
  addCreatedTime?: number;
  /** The added note's author and template, when its detail isn't cached. */
  addNoteRef?: Omit<EventNoteRef, "id">;
}

const getCachedNoteCreatedTime = (noteId: string): number | undefined => {
  const detail = queryClient.getQueryData<NoteDetail>(
    noteQueryKeys.detail(noteId),
  );
  if (detail?.createdDate) return toMs(detail.createdDate);

  for (const [, page] of queryClient.getQueriesData<Page<NoteListItem>>({
    queryKey: noteQueryKeys.lists(),
    exact: false,
  })) {
    const createdDate = page?.data.find(
      (note) => note.id === noteId,
    )?.createdDate;
    if (createdDate) return toMs(createdDate);
  }
  return undefined;
};

/**
 * Rebuild an event's `noteRefs` for its patched `noteIds`. The added note
 * takes its template and author from its cached detail, else from the
 * revision it replaces, so the checklist can match it before a refetch. The
 * first AI generation keeps the note's id and only changes its template, so
 * an added note already in the list is refreshed too. Unchanged refs keep
 * their identity.
 */
const patchNoteRefs = (
  noteRefs: EventNoteRef[],
  noteIds: string[],
  removedIds: Set<string>,
  addNoteId: string | undefined,
  addNoteRef: Omit<EventNoteRef, "id"> | undefined,
): EventNoteRef[] => {
  const refsById = new Map(noteRefs.map((ref) => [ref.id, ref]));
  const replaced = noteRefs.find((ref) => removedIds.has(ref.id));
  return noteIds.map((id) => {
    const existing = refsById.get(id);
    if (id !== addNoteId) return existing ?? { id };
    const detail = queryClient.getQueryData<NoteDetail>(
      noteQueryKeys.detail(id),
    );
    // A revision can change author (AI chat records whoever asked), so the
    // caller's ref and the cached detail win over the revision it replaces.
    const fallback = existing ?? replaced;
    const templateId =
      addNoteRef?.templateId !== undefined
        ? addNoteRef.templateId
        : detail
          ? (detail.template?.id ?? null)
          : fallback?.templateId;
    const providerId =
      addNoteRef?.providerId ?? detail?.providerId ?? fallback?.providerId;
    return existing &&
      existing.templateId === templateId &&
      existing.providerId === providerId
      ? existing
      : { id, templateId, providerId };
  });
};

const sameItems = <T>(a: T[] | undefined, b: T[] | undefined) =>
  a === b ||
  (!!a &&
    !!b &&
    a.length === b.length &&
    a.every((item, index) => item === b[index]));

/** Apply `patchEvent` to every cached event list and event detail. */
const patchCachedEvents = (
  patchEvent: <T extends EventTiny>(event: T) => T,
) => {
  const patchEvents = <T extends EventTiny>(events: T[] | undefined) =>
    events?.map(patchEvent);

  for (const queryKey of [
    eventQueryKeys.lists(),
    eventQueryKeys.byLocations(),
    [...eventQueryKeys.all, "search"] as const,
  ]) {
    queryClient.setQueriesData<EventTiny[]>(
      { queryKey, exact: false },
      patchEvents,
    );
  }
  queryClient.setQueriesData<EventDetail[]>(
    { queryKey: eventQueryKeys.allPatientResults(), exact: false },
    patchEvents,
  );
  queryClient.setQueriesData<EventDetail>(
    { queryKey: eventQueryKeys.details(), exact: false },
    (event) => (event ? patchEvent(event) : event),
  );
  queryClient.setQueriesData<EventTiny>(
    { queryKey: [...eventQueryKeys.all, "tiny"], exact: false },
    (event) => (event ? patchEvent(event) : event),
  );
};

/** A note picked a template in place (before generation): update its event ref. */
const patchEventNoteTemplateInCaches = (noteId: string, templateId: string) =>
  patchCachedEvents((event) =>
    event.noteRefs?.some(
      (ref) => ref.id === noteId && ref.templateId !== templateId,
    )
      ? {
          ...event,
          noteRefs: event.noteRefs.map((ref) =>
            ref.id === noteId ? { ...ref, templateId } : ref,
          ),
        }
      : event,
  );

const patchEventNoteIdsInCaches = ({
  removeNoteIds,
  addEventId,
  addNoteId,
  addCreatedTime,
  addNoteRef,
}: EventNoteIdsPatch) => {
  const removeIds = new Set(removeNoteIds);
  const patchEvent = <T extends EventTiny>(event: T): T => {
    const existingNoteIds = event.noteIds ?? [];
    const filteredNoteIds = existingNoteIds.filter(
      (id) => !removeIds.has(id) && id !== addNoteId,
    );
    const nextNoteIds = [...filteredNoteIds];
    if (addEventId === event.id && addNoteId) {
      const resolvedAddCreatedTime =
        addCreatedTime ?? getCachedNoteCreatedTime(addNoteId);
      const insertionIndex =
        resolvedAddCreatedTime === undefined
          ? -1
          : nextNoteIds.findIndex((id) => {
              const existingCreatedTime = getCachedNoteCreatedTime(id);
              return existingCreatedTime === undefined
                ? addCreatedTime !== undefined
                : resolvedAddCreatedTime > existingCreatedTime;
            });
      nextNoteIds.splice(
        insertionIndex === -1 ? nextNoteIds.length : insertionIndex,
        0,
        addNoteId,
      );
    }

    const nextNoteRefs =
      event.noteRefs &&
      patchNoteRefs(
        event.noteRefs,
        nextNoteIds,
        removeIds,
        addNoteId,
        addNoteRef,
      );
    if (
      sameItems(nextNoteIds, existingNoteIds) &&
      sameItems(nextNoteRefs, event.noteRefs)
    ) {
      return event;
    }
    return {
      ...event,
      noteIds: nextNoteIds,
      ...(nextNoteRefs && { noteRefs: nextNoteRefs }),
    };
  };
  patchCachedEvents(patchEvent);
};

const getCachedNoteChainIds = (
  id: string,
  parentNoteId?: string,
): { chainIds: Set<string>; parentNoteId?: string; eventId?: string } => {
  const anchor = queryClient.getQueryData<NoteDetail>(noteQueryKeys.detail(id));
  const resolvedParentNoteId = parentNoteId ?? anchor?.parentNoteId;
  const chainIds = new Set<string>([id]);
  let eventId = anchor?.eventId;
  if (!resolvedParentNoteId) return { chainIds, eventId };

  chainIds.add(resolvedParentNoteId);
  const revisions = queryClient.getQueryData<NoteSummary[]>(
    noteQueryKeys.revisions({ parentNoteId: resolvedParentNoteId }),
  );
  for (const revision of revisions ?? []) chainIds.add(revision.id);
  for (const [, detail] of queryClient.getQueriesData<NoteDetail>({
    queryKey: [...noteQueryKeys.all, "detail"],
    exact: false,
  })) {
    if (detail?.parentNoteId !== resolvedParentNoteId) continue;
    chainIds.add(detail.id);
    eventId ??= detail.eventId;
  }
  return { chainIds, parentNoteId: resolvedParentNoteId, eventId };
};

const syncEventNoteIdsFromChain = ({
  id,
  parentNoteId,
  eventId,
  createdDate,
  noteRef,
}: {
  id: string;
  parentNoteId: string;
  eventId?: string;
  createdDate?: string;
  noteRef?: Omit<EventNoteRef, "id">;
}) => {
  const cachedChain = getCachedNoteChainIds(id, parentNoteId);
  patchEventNoteIdsInCaches({
    removeNoteIds: cachedChain.chainIds,
    addEventId: eventId ?? cachedChain.eventId,
    addNoteId: id,
    addCreatedTime: createdDate ? toMs(createdDate) : undefined,
    addNoteRef: noteRef,
  });
};

// Shared so several failed adds collapse into one toast that callers can replace.
export const ADD_FROM_URL_ERROR_TOAST_ID = "add-attachment-from-url-error";

export const seedPatientCachesFromNotePatient = (
  patient: NoteDetail["patient"] | undefined,
) => {
  if (!patient?.id) return false;

  const update = Object.fromEntries(
    Object.entries(patient).filter(([, value]) => value != null),
  ) as Partial<PatientDetail>;

  if (Object.keys(update).length === 0) return false;

  updatePatientInCache(patient.id, update);
  return true;
};

// Compute the per-mark diff client-side and send it in a single request. The
// server applies all three lists transactionally and produces one history
// revision, so concurrent edits to different marks can't clobber each other
// and partial-save errors are impossible.
export const useAnnotationMutation = (
  patientId: string,
  noteId?: string | null,
) => {
  // The annotator scopes its lot picker to this location, so the recomputed
  // doses must draw against the same one — otherwise a shipment-backed mark
  // can't claim a lot today's checkout already drew and deducts it twice.
  const { currentLocationId } = useUser();
  // A save can finish after the caller has moved to another note, and React
  // Query runs it with the hook's latest arguments; a caller that saves in the
  // background passes the patient and note it saved for in the variables.
  const scopeOf = (variables: {
    patientId?: string;
    noteId?: string | null;
  }) => ({
    patientId: variables.patientId ?? patientId,
    noteId: variables.noteId !== undefined ? variables.noteId : noteId,
  });
  return useMutation({
    mutationFn: async ({
      attachmentId,
      annotations,
      baseline,
    }: {
      attachmentId: string;
      annotations: Annotation[];
      baseline: Annotation[];
      // Skip the success toast for saves the provider didn't click.
      silent?: boolean;
      patientId?: string;
      noteId?: string | null;
    }) => {
      if (attachmentId.startsWith("template-placeholder-")) {
        throw new Error(
          "We couldn't prepare this template image. Your marks are still on screen—try saving again.",
        );
      }

      const { created, updated, deletedIds } = diffAnnotations(
        baseline,
        annotations,
      );
      // Strip server-stamped audit fields from patches so they're not sent
      // back (the server rejects them as immutable anyway).
      const updatedPatches = updated.map((ann) => {
        const {
          type: _t,
          createdAt: _c,
          createdBy: _cb,
          updatedAt: _u,
          updatedBy: _ub,
          ...patch
        } = ann as Record<string, any>;
        return patch;
      });
      return await applyAttachmentAnnotationsDiff(attachmentId, {
        created,
        updated: updatedPatches,
        deletedIds,
        locationId: currentLocationId || undefined,
      });
    },
    onMutate: async (variables) => {
      const { patientId, noteId } = scopeOf(variables);
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: noteQueryKeys.attachment(variables.attachmentId),
        }),
        cancelAttachmentQueries(patientId, noteId),
        // A chart refetch in flight would land after our dose seed and
        // overwrite it with lagging-replica data — cancel it like the Add Dose
        // mutation does.
        patientId
          ? queryClient.cancelQueries({
              queryKey: patientQueryKeys.detail(patientId),
            })
          : Promise.resolve(),
      ]);
      const seedTicket = claimAnnotatorSeedTicket(patientId);
      const previousAttachment =
        queryClient.getQueryData<NoteAttachment | null>(
          noteQueryKeys.attachment(variables.attachmentId),
        );
      queryClient.setQueryData<NoteAttachment | null>(
        noteQueryKeys.attachment(variables.attachmentId),
        (oldData) =>
          oldData
            ? { ...oldData, annotations: variables.annotations }
            : oldData,
      );
      updateAttachmentAnnotationCaches(
        patientId,
        noteId,
        variables.attachmentId,
        { annotations: variables.annotations },
      );
      return { previousAttachment, seedTicket };
    },
    onSuccess: async (savedAttachment, variables, context) => {
      const { patientId, noteId } = scopeOf(variables);
      if (savedAttachment) {
        seedSavedAttachmentInCaches(
          patientId,
          noteId,
          variables.attachmentId,
          savedAttachment,
        );
        // Face-chart marks create doses server-side. Seed them straight into the
        // patient chart cache (rather than refetching, which can read a lagging
        // replica) so the note's medication question updates without a refresh.
        seedAnnotatorDosesInPatientChart(
          patientId,
          savedAttachment,
          context?.seedTicket,
        );
      }

      if (!variables.silent) {
        toast({
          title: "Success",
          description: "Annotations saved successfully",
          variant: "success",
        });
      }
    },
    onError: (err, variables, context) => {
      const { patientId, noteId } = scopeOf(variables);
      if (context?.previousAttachment !== undefined) {
        queryClient.setQueryData(
          noteQueryKeys.attachment(variables.attachmentId),
          context.previousAttachment,
        );
      }
      queryClient.invalidateQueries({
        queryKey: noteQueryKeys.attachment(variables.attachmentId),
      });
      invalidateAttachmentCaches(patientId, noteId);

      toast({
        title: "Save failed",
        description:
          err instanceof Error
            ? err.message
            : "Failed to save annotations. Please try again.",
        variant: "error",
      });
    },
  });
};

// Unified restore annotation mutation that works for both note context and standalone
export const useRestoreAnnotationMutation = (
  patientId: string,
  noteId?: string | null,
) => {
  return useMutation({
    mutationFn: async ({
      attachmentId,
      historyId,
    }: {
      attachmentId: string;
      historyId: string;
    }) => await restoreAttachmentAnnotations(attachmentId, historyId),
    onSuccess: async (_, variables) => {
      // Invalidate history query for this attachment so history modal shows updated image
      await queryClient.invalidateQueries({
        queryKey: noteQueryKeys.attachmentAnnotationHistory(
          variables.attachmentId,
        ),
      });

      invalidateAttachmentCaches(patientId, noteId);

      toast({
        title: "Success",
        description: "Annotations restored successfully",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Restore failed",
        description: "Failed to restore annotations. Please try again.",
        variant: "error",
      });
    },
  });
};

// Duplicate an existing attachment (copies image, annotations, and tags)
export const useDuplicateAttachmentMutation = (
  patientId: string,
  noteId?: string | null,
) => {
  return useMutation<NoteAttachment | null, Error, string>({
    mutationFn: async (attachmentId: string) =>
      await duplicateNoteAttachment(attachmentId),
    onSuccess: async (data) => {
      if (data && patientId) {
        addAttachmentsToCache(patientId, noteId, [data]);
        optimisticAdjustSidebarCounts(patientId, { attachmentsCount: 1 });
      }
      toast({
        title: "Success",
        description: "Attachment duplicated",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to duplicate attachment",
        variant: "error",
      });
    },
  });
};

// Helper to create optimistic attachment entry
const createOptimisticAttachment = (
  file: File,
  tempId: string,
  name?: string,
): NoteAttachment => {
  const blobUrl = URL.createObjectURL(file);
  return {
    id: tempId,
    noteId: "",
    gcsPath: "",
    originalFilename: file.name,
    signedUrl: blobUrl,
    thumbnailSignedUrl: blobUrl,
    annotations: [],
    name: name || file.name,
    createdDate: new Date().toISOString(),
  };
};

/**
 * Helper to update attachment caches (both infinite gallery and note-specific)
 * If patientId/noteId are provided, only those specific caches are updated.
 * Otherwise, all matching caches are updated.
 */
const updateAttachmentsCache = (
  _patientId: string | null | undefined,
  _noteId: string | null | undefined,
  updateFn: (data: NoteAttachment[]) => NoteAttachment[],
) => {
  // Update every loaded infinite gallery query. The gallery may be keyed by
  // patient, global view, tags, social opt-in, and/or a date-range suffix; a
  // save from the full-screen editor should refresh whichever list the user
  // returns to as long as that list contains the edited attachment.
  queryClient.setQueriesData<InfiniteData<AttachmentDayPage>>(
    {
      queryKey: [...noteQueryKeys.all, "attachments-infinite"],
      exact: false,
    },
    (oldData) => {
      if (!oldData?.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          data: updateFn(page.data),
        })),
      };
    },
  );

  // Update every loaded note attachment query too; the update function only
  // changes matching ids, so broad cache seeding is safe and prevents stale
  // previews when the same attachment is visible in multiple contexts.
  queryClient.setQueriesData<NoteAttachment[]>(
    {
      queryKey: [...noteQueryKeys.all, "attachments"],
      exact: false,
    },
    (oldData) => {
      if (!oldData || !Array.isArray(oldData)) return oldData;
      return updateFn(oldData);
    },
  );
};

const attachmentMatchesId = (
  attachment: NoteAttachment,
  attachmentId: string,
) => attachment.id === attachmentId || attachment.attachmentId === attachmentId;

const replaceMatchingAttachments = (
  attachments: NoteAttachment[],
  previousAttachmentId: string,
  savedAttachment: NoteAttachment,
) =>
  attachments.map((attachment) =>
    attachmentMatchesId(attachment, previousAttachmentId) ||
    attachmentMatchesId(attachment, savedAttachment.id)
      ? {
          ...attachment,
          ...savedAttachment,
          attachmentId: savedAttachment.attachmentId || savedAttachment.id,
          url:
            savedAttachment.signedUrl || savedAttachment.url || attachment.url,
          thumbnailUrl:
            savedAttachment.thumbnailSignedUrl ||
            savedAttachment.thumbnailUrl ||
            attachment.thumbnailUrl,
        }
      : attachment,
  );

const updateAttachmentAnnotationCaches = (
  patientId: string | null | undefined,
  noteId: string | null | undefined,
  attachmentId: string,
  patch: Pick<NoteAttachment, "annotations">,
) => {
  updateAttachmentsCache(patientId, noteId, (attachments) =>
    attachments.map((attachment) =>
      attachmentMatchesId(attachment, attachmentId)
        ? { ...attachment, ...patch }
        : attachment,
    ),
  );
};

const seedSavedAttachmentInCaches = (
  patientId: string | null | undefined,
  noteId: string | null | undefined,
  previousAttachmentId: string,
  savedAttachment: NoteAttachment,
) => {
  queryClient.setQueryData<NoteAttachment | null>(
    noteQueryKeys.attachment(previousAttachmentId),
    savedAttachment,
  );
  queryClient.setQueryData<NoteAttachment | null>(
    noteQueryKeys.attachment(savedAttachment.id),
    savedAttachment,
  );
  updateAttachmentsCache(patientId, noteId, (attachments) =>
    replaceMatchingAttachments(
      attachments,
      previousAttachmentId,
      savedAttachment,
    ),
  );
};

// Per-patient ticket for annotation saves: each save claims one on mutate and
// only the newest claimant is allowed to seed doses when its response lands.
const annotatorSeedTickets = new Map<string, number>();
let annotatorSeedCounter = 0;

const claimAnnotatorSeedTicket = (patientId: string | null | undefined) => {
  const ticket = ++annotatorSeedCounter;
  if (patientId) annotatorSeedTickets.set(patientId, ticket);
  return ticket;
};

const isNewestAnnotatorSeed = (patientId: string, seedTicket?: number) =>
  seedTicket === undefined ||
  annotatorSeedTickets.get(patientId) === seedTicket;

function invalidateNoteDoseCaches(patientId: string) {
  queryClient.invalidateQueries({ queryKey: medicationsQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: stockQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: ["lot-numbers"] });
  queryClient.invalidateQueries({
    queryKey: patientQueryKeys.editHistory(patientId),
  });
  queryClient.invalidateQueries({
    queryKey: todaysItemsQueryKeys.patient(patientId),
  });
}

/**
 * Seed recomputed face-chart doses into the patient chart: remove archived IDs,
 * then prepend the new doses so the note's dose table updates without a refresh.
 */
const seedAnnotatorDosesInPatientChart = (
  patientId: string | null | undefined,
  savedAttachment: NoteAttachment,
  seedTicket?: number,
) => {
  const createdDoses = savedAttachment.createdDoses ?? [];
  const archivedDoseIds = savedAttachment.archivedDoseIds ?? [];
  if (!patientId || (!createdDoses.length && !archivedDoseIds.length)) return;
  // Saves on one patient can overlap (an autosave plus an explicit save, or a
  // quick erase after a save). Only the newest one may seed: an older response
  // arriving late would re-add a dose the newer save just archived.
  if (!isNewestAnnotatorSeed(patientId, seedTicket)) return;

  const removedIds = new Set([
    ...archivedDoseIds,
    ...createdDoses.map((dose) => dose.id),
  ]);
  queryClient.setQueryData<PatientChart>(
    patientQueryKeys.detail(patientId),
    (previous) => {
      if (!previous) return previous;
      const kept = (previous.medications || []).filter(
        (medication) => !removedIds.has(medication.id),
      );
      return { ...previous, medications: [...createdDoses, ...kept] };
    },
  );

  // A recomputed dose changes both its clinical documentation and physical ledger.
  invalidateNoteDoseCaches(patientId);
};

/**
 * Moving or deleting a face chart rewrites the comment on the doses it wrote, so
 * they stop linking to it. Mirror that into every cached patient chart holding
 * those doses, so an open Dose Log drops the link without a refetch.
 */
export const detachChartDosesInPatientCharts = (
  detached: DetachedChartDoses | null | undefined,
) => {
  if (!detached?.doseIds.length) return;
  const doseIds = new Set(detached.doseIds);
  queryClient.setQueriesData<PatientChart>(
    { queryKey: [...patientQueryKeys.all, "detail"] },
    (previous) => {
      if (!previous?.medications?.some((dose) => doseIds.has(dose.id))) {
        return previous;
      }
      return {
        ...previous,
        medications: previous.medications.map((dose) =>
          doseIds.has(dose.id)
            ? { ...dose, comment: detached.comment, chartAttachmentId: null }
            : dose,
        ),
      };
    },
  );
};

/**
 * Helper to invalidate attachment caches
 */
const invalidateAttachmentCaches = (
  patientId: string | null | undefined,
  noteId: string | null | undefined,
) => {
  if (patientId) {
    queryClient.invalidateQueries({
      queryKey: noteQueryKeys.attachmentsInfinite({ patientId }),
      exact: false,
    });
  }
  // Always invalidate global infinite query to be safe, especially for standalone cases
  queryClient.invalidateQueries({
    queryKey: noteQueryKeys.attachmentsInfinite({}),
    exact: false,
  });

  if (noteId) {
    queryClient.invalidateQueries({
      queryKey: noteQueryKeys.attachments({ noteId }),
    });
  }
};

/**
 * Helper to cancel outgoing attachment queries
 */
const cancelAttachmentQueries = async (
  patientId: string | null | undefined,
  noteId: string | null | undefined,
) => {
  await queryClient.cancelQueries({
    queryKey: patientId
      ? noteQueryKeys.attachmentsInfinite({ patientId })
      : [...noteQueryKeys.all, "attachments-infinite"],
    exact: false,
  });
  await queryClient.cancelQueries({
    queryKey: noteId
      ? noteQueryKeys.attachments({ noteId })
      : [...noteQueryKeys.all, "attachments"],
    exact: false,
  });
};

const getAttachmentCacheDate = (
  noteId: string | null | undefined,
  attachments: NoteAttachment[],
) => {
  if (noteId) {
    const note = queryClient.getQueryData<NoteDetail>(
      noteQueryKeys.detail(noteId),
    );
    const noteDate = note?.visitDate ?? note?.createdDate;
    if (noteDate) return new Date(noteDate);
  }

  const attachmentDate = attachments.find(
    (attachment) => attachment.createdDate,
  )?.createdDate;
  if (attachmentDate) return new Date(attachmentDate);

  return new Date();
};

// The clinic's day, which is how the backend pages the gallery.
const dateKey = (date: Date) => clinicClock().dayOf(date);

/**
 * Helper to add new attachments to caches
 */
const addAttachmentsToCache = (
  patientId: string,
  noteId: string | null | undefined,
  newAttachments: NoteAttachment[],
) => {
  const attachmentDate = getAttachmentCacheDate(noteId, newAttachments);
  const attachmentDateKey = dateKey(attachmentDate);
  const attachmentTimestamp = attachmentDate.toISOString();
  const datedAttachments = newAttachments.map((attachment) => ({
    ...attachment,
    createdDate: noteId
      ? attachmentTimestamp
      : (attachment.createdDate ?? attachmentTimestamp),
  }));

  // Update Infinite Query with page creation logic
  queryClient.setQueriesData<InfiniteData<AttachmentDayPage>>(
    {
      queryKey: noteQueryKeys.attachmentsInfinite({ patientId }),
      exact: false,
    },
    (oldData) => {
      // A gallery that never fetched must stay unloaded; seeding it with just
      // these photos would hide the rest of the patient's gallery until stale.
      if (!oldData?.pages) return oldData;

      const pages = [...oldData.pages];
      const targetPageIndex = pages.findIndex(
        // Page dates are the API's bare "YYYY-MM-DD" day keys. Reparsing one
        // with `new Date` reads it as UTC midnight, the previous day west of UTC.
        (page) => page.date === attachmentDateKey,
      );

      if (targetPageIndex >= 0) {
        pages[targetPageIndex] = {
          ...pages[targetPageIndex],
          data: [...datedAttachments, ...pages[targetPageIndex].data],
        };
      } else {
        pages.push({
          date: attachmentDateKey,
          data: datedAttachments,
          nextDate: null,
        });
        pages.sort((a, b) => b.date.localeCompare(a.date));
        for (let i = 0; i < pages.length; i++) {
          const nextDate = pages[i + 1]?.date || null;
          if (pages[i].nextDate !== nextDate) {
            pages[i] = {
              ...pages[i],
              nextDate,
            };
          }
        }
      }

      return {
        ...oldData,
        pages,
        pageParams: pages.map((_, index) =>
          index === 0 ? undefined : pages[index - 1]?.nextDate,
        ),
      };
    },
  );

  // Update Note Query
  if (noteId) {
    queryClient.setQueryData<NoteAttachment[]>(
      noteQueryKeys.attachments({ noteId }),
      (oldData) =>
        oldData ? [...datedAttachments, ...oldData] : datedAttachments,
    );
  }
};

export const __testing = {
  addAttachmentsToCache,
  dateKey,
  getCachedNoteChainIds,
  patchEventNoteIdsInCaches,
};

/**
 * Updates matching attachments in the infinite gallery cache in place (by id)
 * without shifting their position. Use this when the attachment already exists
 * in the gallery and only some fields (e.g. `note`) need to change — appending
 * via `addAttachmentsToCache` would create visible duplicates.
 */
export const updateAttachmentsInInfiniteGalleryCache = (
  patientId: string,
  updater: (attachment: NoteAttachment) => NoteAttachment,
  shouldUpdate: (attachment: NoteAttachment) => boolean,
) => {
  queryClient.setQueriesData<InfiniteData<AttachmentDayPage>>(
    {
      queryKey: noteQueryKeys.attachmentsInfinite({ patientId }),
      exact: false,
    },
    (oldData) => {
      if (!oldData?.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          data: page.data.map((att) =>
            shouldUpdate(att) ? updater(att) : att,
          ),
        })),
      };
    },
  );
};

/**
 * Helper to remove attachments from caches
 */
const removeAttachmentsFromCache = (
  patientId: string,
  noteId: string | null | undefined,
  attachmentIds: string[],
) => {
  const idsSet = new Set(attachmentIds);
  updateAttachmentsCache(patientId, noteId, (data) =>
    data.filter((a) => !idsSet.has(a.id)),
  );
};

const sortDocumentsByCreatedDateDesc = (documents: DocumentSummary[]) =>
  [...documents].sort((a, b) => {
    const aTime = a.createdDate ? toMs(a.createdDate) : 0;
    const bTime = b.createdDate ? toMs(b.createdDate) : 0;
    return bTime - aTime;
  });

const mergeDocumentsIntoNoteCache = (
  noteId: string,
  documents: DocumentSummary[],
) => {
  if (documents.length === 0) return;
  queryClient.setQueryData<DocumentSummary[] | null>(
    noteQueryKeys.noteDocuments(noteId),
    (oldData) => {
      if (!oldData) return documents;
      const byId = new Map(oldData.map((doc) => [doc.id, doc]));
      documents.forEach((doc) => byId.set(doc.id, doc));
      return sortDocumentsByCreatedDateDesc(Array.from(byId.values()));
    },
  );
};

const sortTagsByName = (tags: NoteAttachmentTag[]) =>
  [...tags].sort((a, b) => a.name.localeCompare(b.name));

const updateTagsCache = (
  updater: (tags: NoteAttachmentTag[]) => NoteAttachmentTag[],
) => {
  queryClient.setQueryData<
    Page<NoteAttachmentTag> | NoteAttachmentTag[] | null
  >(noteQueryKeys.tags(), (oldData) => {
    if (!oldData) return oldData;
    if (Array.isArray(oldData)) return sortTagsByName(updater(oldData));
    return {
      ...oldData,
      data: sortTagsByName(updater(oldData.data)),
    };
  });
};

const updateEmbeddedAttachmentTags = (
  updater: (tags: NoteAttachmentTag[]) => NoteAttachmentTag[],
) => {
  updateAttachmentsCache(null, null, (attachments) =>
    attachments.map((attachment) => ({
      ...attachment,
      tags: updater(attachment.tags ?? []),
    })),
  );
};

const patchTemplatePatientViewCaches = (data: NoteTemplateTableItem) => {
  queryClient.setQueryData<NoteTemplateTableItem[] | null>(
    noteQueryKeys.templates(),
    (oldData) => {
      if (!oldData) return oldData;
      return oldData.map((template) =>
        template.id === data.id ? { ...template, ...data } : template,
      );
    },
  );

  queryClient.setQueryData<NoteTemplateTableItem | null>(
    noteQueryKeys.templateDetail(data.id),
    (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        visibleToPatient: data.visibleToPatient,
        patientVisibleQuestionOrder: data.patientVisibleQuestionOrder,
        showSummaryInPatientView: data.showSummaryInPatientView,
      };
    },
  );
};

const patchSignedOffNoteInListCaches = (note: NoteDetail) => {
  queryClient.setQueriesData<Page<NoteListItem>>(
    { queryKey: noteQueryKeys.lists(), exact: false },
    (oldData) => {
      if (!oldData?.data) return oldData;
      let changed = false;
      const data = oldData.data.map((item) => {
        if (item.id !== note.id) return item;
        changed = true;
        return {
          ...item,
          signedOffProviderId: note.signedOffProviderId,
          signedOffDate: note.signedOffDate,
          coSignedProviderId: note.coSignedProviderId,
          coSignedDate: note.coSignedDate,
          requiresCoSign: note.requiresCoSign,
          signOff: note.signOff ?? item.signOff,
          // Signing completes the note server-side; mirror that so the row does
          // not keep showing a Draft badge until the next fetch.
          completedDate: note.completedDate ?? item.completedDate,
        };
      });
      return changed ? { ...oldData, data } : oldData;
    },
  );
};

const patchSignedOffNoteInRevisionCaches = (note: NoteDetail) => {
  queryClient.setQueryData<NoteSummary[]>(
    noteQueryKeys.revisions({ parentNoteId: note.parentNoteId }),
    (oldData) => {
      if (!oldData) return oldData;
      return oldData.map((revision) =>
        revision.id === note.id
          ? {
              ...revision,
              signedOffProviderId: note.signedOffProviderId,
              signedOffDate: note.signedOffDate,
            }
          : revision,
      );
    },
  );
};

// Unified attachment mutation that works for both note context and standalone
export const useAttachmentMutation = (
  patientId: string,
  noteId?: string | null,
) => {
  return useMutation<
    NoteAttachment | null,
    Error,
    { file: File; name?: string; answerId?: string },
    {
      previousSidebarCounts?: PatientSidebarCounts;
      tempId?: string;
      blobUrl?: string;
    }
  >({
    mutationFn: async ({
      file,
      name,
      answerId,
    }: {
      file: File;
      name?: string;
      answerId?: string;
    }) => {
      if (!patientId) return null;
      return await addNoteAttachment(
        patientId,
        file,
        noteId || null,
        name,
        answerId,
      );
    },
    onMutate: async ({ file, name }) => {
      if (!patientId) return {};

      await cancelAttachmentQueries(patientId, noteId);

      // Create optimistic attachment
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimisticAttachment = createOptimisticAttachment(
        file,
        tempId,
        name,
      );

      addAttachmentsToCache(patientId, noteId, [optimisticAttachment]);

      const previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
        attachmentsCount: 1,
      });

      return {
        previousSidebarCounts,
        tempId,
        blobUrl: optimisticAttachment.signedUrl,
      };
    },
    onSuccess: async (data, _vars, ctx) => {
      // Revoke blob URL to free memory
      if (ctx?.blobUrl) {
        URL.revokeObjectURL(ctx.blobUrl);
      }

      // Immediately replace optimistic entry with real data or remove if failed
      if (ctx?.tempId) {
        if (data) {
          removeAttachmentsFromCache(patientId, noteId, [ctx.tempId]);
          addAttachmentsToCache(patientId, noteId, [data]);
        } else {
          // Remove optimistic entry if data is null (server returned 200 but failed to process)
          if (patientId) {
            removeAttachmentsFromCache(patientId, noteId, [ctx.tempId]);
            optimisticAdjustSidebarCounts(patientId, { attachmentsCount: -1 });
          }

          toast({
            title: "Error",
            description: "Failed to upload attachment",
            variant: "error",
          });
        }
      }

      // DON'T invalidate queries here - it would remove other pending optimistic entries
      // The direct cache update above is sufficient. A full refresh can happen when user navigates away/back.
    },
    onError: (_err, _vars, ctx) => {
      // Revoke blob URL to free memory
      if (ctx?.blobUrl) {
        URL.revokeObjectURL(ctx.blobUrl);
      }

      // Roll back the optimistic insert by removing the temp entry.
      if (patientId && ctx?.tempId) {
        removeAttachmentsFromCache(patientId, noteId, [ctx.tempId]);
      }

      if (patientId) revertSidebarCounts(patientId, ctx?.previousSidebarCounts);
      toast({
        title: "Error",
        description: "Failed to upload attachment",
        variant: "error",
      });
    },
  });
};

// Signed URL upload mutation — uploads directly to GCS, bypassing the server
export const useSignedUrlUploadMutation = (
  patientId: string,
  noteId?: string | null,
) => {
  return useMutation<
    NoteAttachment[],
    Error,
    {
      files: File[];
      names?: (string | undefined)[];
      onProgress?: (fileIndex: number, loaded: number, total: number) => void;
    },
    {
      previousSidebarCounts?: PatientSidebarCounts;
      tempIds?: string[];
      blobUrls?: string[];
    }
  >({
    mutationFn: async ({ files, names, onProgress }) => {
      if (!patientId || files.length === 0) return [];

      const fileContentTypes = files.map(
        (file) => file.type || "application/octet-stream",
      );

      const initResult = await bulkInitiateAttachmentUpload({
        patientId,
        noteId: noteId ?? null,
        files: files.map((file, i) => ({
          filename: file.name,
          contentType: fileContentTypes[i]!,
          name: names?.[i] || file.name,
        })),
      });
      if (!initResult || initResult.uploads.length !== files.length) {
        throw new Error("Failed to initiate upload");
      }

      const settled = await Promise.allSettled(
        files.map((file, i) =>
          uploadToGcs(
            initResult.uploads[i]!.uploadUrl,
            file,
            fileContentTypes[i]!,
            onProgress
              ? (loaded, total) => onProgress(i, loaded, total)
              : undefined,
          ).then(() => initResult.uploads[i]!.attachmentId),
        ),
      );

      const uploadedIds: string[] = [];
      for (const result of settled) {
        if (result.status === "fulfilled") {
          uploadedIds.push(result.value);
        } else {
          console.error("Upload failed:", result.reason);
        }
      }

      if (uploadedIds.length === 0) {
        throw new Error("All uploads failed");
      }

      const finalResult = await bulkFinalizeAttachmentUpload(uploadedIds);
      if (!finalResult) throw new Error("Failed to finalize upload");

      return finalResult.attachments;
    },
    onMutate: async ({ files, names }) => {
      if (!patientId) return {};

      await cancelAttachmentQueries(patientId, noteId);

      const blobUrls: string[] = [];
      const tempIds: string[] = [];
      const optimisticAttachments: NoteAttachment[] = [];

      files.forEach((file, index) => {
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}-${index}`;
        tempIds.push(tempId);
        const blobUrl = URL.createObjectURL(file);
        blobUrls.push(blobUrl);
        optimisticAttachments.push({
          id: tempId,
          noteId: "",
          gcsPath: "",
          originalFilename: file.name,
          signedUrl: blobUrl,
          thumbnailSignedUrl: blobUrl,
          annotations: [],
          name: names?.[index] || file.name,
          createdDate: new Date().toISOString(),
        });
      });

      addAttachmentsToCache(patientId, noteId, optimisticAttachments);

      const previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
        attachmentsCount: files.length,
      });

      return { previousSidebarCounts, tempIds, blobUrls };
    },
    onSuccess: (data, _vars, ctx) => {
      ctx?.blobUrls?.forEach((url) => URL.revokeObjectURL(url));

      if (!ctx?.tempIds) return;

      if (data.length === 0) {
        // Finalize returned no attachments (e.g. backend archived an
        // oversized upload). Roll back the optimistic temp entries and
        // decrement the sidebar bump by exactly what onMutate added,
        // so concurrent updates aren't clobbered.
        if (patientId) {
          removeAttachmentsFromCache(patientId, noteId, ctx.tempIds);
          optimisticAdjustSidebarCounts(patientId, {
            attachmentsCount: -ctx.tempIds.length,
          });
        }
        return;
      }

      removeAttachmentsFromCache(patientId, noteId, ctx.tempIds);
      addAttachmentsToCache(patientId, noteId, data);

      // A gallery upload can be filed onto the uploader's note for today by the
      // server; refresh that note's photos so an open note shows them without a reload.
      if (!noteId) {
        new Set(
          data.flatMap((attachment) =>
            attachment.noteId ? [attachment.noteId] : [],
          ),
        ).forEach((linkedNoteId) =>
          queryClient.invalidateQueries({
            queryKey: noteQueryKeys.attachments({ noteId: linkedNoteId }),
          }),
        );
      }

      // Adjust sidebar count if some uploads failed
      const diff = (ctx.tempIds?.length ?? 0) - data.length;
      if (diff > 0) {
        optimisticAdjustSidebarCounts(patientId, {
          attachmentsCount: -diff,
        });
      }
    },
    onError: (err, _vars, ctx) => {
      ctx?.blobUrls?.forEach((url) => URL.revokeObjectURL(url));

      if (patientId && ctx?.tempIds) {
        removeAttachmentsFromCache(patientId, noteId, ctx.tempIds);
      }
      if (patientId) revertSidebarCounts(patientId, ctx?.previousSidebarCounts);
      toast(
        isPermissionDeniedError(err)
          ? PHOTO_PERMISSION_DENIED_TOAST
          : {
              title: "Error",
              description: "Failed to upload files",
              variant: "error",
            },
      );
    },
  });
};

// Link existing attachments to a note without re-uploading
export const useLinkAttachmentsMutation = (
  patientId: string,
  noteId: string,
) => {
  return useMutation<
    NoteAttachment[] | null,
    Error,
    { attachments: NoteAttachment[] },
    { previousNoteAttachments?: NoteAttachment[] }
  >({
    mutationFn: async ({ attachments }) => {
      if (attachments.length === 0) return null;
      return await linkAttachmentsToNote(
        noteId,
        attachments.map((a) => a.id),
      );
    },
    // The photos are already uploaded, so show them on the note straight away.
    onMutate: async ({ attachments }) => {
      await cancelAttachmentQueries(patientId, noteId);
      const queryKey = noteQueryKeys.attachments({ noteId });
      const previousNoteAttachments =
        queryClient.getQueryData<NoteAttachment[]>(queryKey);
      if (previousNoteAttachments) {
        const existingIds = new Set(previousNoteAttachments.map((a) => a.id));
        queryClient.setQueryData<NoteAttachment[]>(queryKey, [
          ...attachments.filter((a) => !existingIds.has(a.id)),
          ...previousNoteAttachments,
        ]);
      }
      return { previousNoteAttachments };
    },
    onSuccess: async (data) => {
      if (data && data.length > 0) {
        // Swap the optimistic entries for the server payload in place. The
        // attachments already exist in the infinite gallery cache (we're
        // linking existing gallery photos), so update those in place too
        // rather than prepending duplicates.
        const updatedById = new Map(data.map((att) => [att.id, att]));
        queryClient.setQueryData<NoteAttachment[]>(
          noteQueryKeys.attachments({ noteId }),
          (oldData) => {
            if (!oldData) return data;
            const missing = data.filter(
              (att) => !oldData.some((a) => a.id === att.id),
            );
            return [
              ...missing,
              ...oldData.map((att) => updatedById.get(att.id) ?? att),
            ];
          },
        );
        updateAttachmentsInInfiniteGalleryCache(
          patientId,
          (att) => updatedById.get(att.id) ?? att,
          (att) => updatedById.has(att.id),
        );
      }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousNoteAttachments) {
        queryClient.setQueryData(
          noteQueryKeys.attachments({ noteId }),
          ctx.previousNoteAttachments,
        );
      }
      toast({
        title: "Error",
        description: "Failed to add photos to note",
        variant: "error",
      });
    },
  });
};

export const useMutationQueries = () => {
  const { selectedNote } = useSelectedNote();
  const patientId = selectedNote?.patient?.id;

  const validNoteId = useMemo(() => {
    return !isLocalNoteId(selectedNote?.id) ? selectedNote?.id || null : null;
  }, [selectedNote?.id]);

  const uploadAttachmentMutation = useAttachmentMutation(
    patientId || "",
    validNoteId || null,
  );

  const addFromUrlMutation = useMutation<
    NoteAttachment | null,
    Error,
    {
      gcsPath: string;
      name?: string;
      answerId: string;
    },
    { previousSidebarCounts?: PatientSidebarCounts }
  >({
    mutationFn: async ({
      gcsPath,
      name,
      answerId,
    }: {
      gcsPath: string;
      name?: string;
      answerId: string;
    }) => {
      if (!validNoteId) return null;
      return await addNoteAttachmentFromUrl(
        validNoteId,
        gcsPath,
        answerId,
        name,
      );
    },
    onMutate: async () => {
      if (!patientId) return {};
      const previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
        attachmentsCount: 1,
      });
      return { previousSidebarCounts };
    },
    onSuccess: async (data) => {
      // Add the new attachment directly to the cache instead of invalidating
      // This prevents flickering when converting placeholders
      if (data && patientId) {
        addAttachmentsToCache(patientId, validNoteId, [data]);
      }
      // DON'T invalidate queries - direct cache update is sufficient
    },
    onError: (_err, _vars, ctx) => {
      if (patientId) revertSidebarCounts(patientId, ctx?.previousSidebarCounts);
      toast({
        id: ADD_FROM_URL_ERROR_TOAST_ID,
        title: "Error",
        description: "Couldn't add the image. Please try again.",
        variant: "error",
      });
    },
  });

  const deleteAttachmentMutation = useMutation<
    DetachedChartDoses | null,
    Error,
    string,
    {
      previousSidebarCounts?: PatientSidebarCounts;
      attachmentId?: string;
      previousInfinite?: Array<[readonly unknown[], unknown]>;
      previousNoteAttachments?: NoteAttachment[];
      mutationNoteId?: string | null;
      mutationPatientId?: string;
    }
  >({
    mutationFn: async (attachmentId: string) =>
      await deleteNoteAttachment(attachmentId),
    onMutate: async (attachmentId) => {
      if (!patientId) return {};

      // Capture the note/patient ids at mutate time so rollback targets the
      // correct caches even if the user selects a different note or patient
      // mid-request.
      const mutationNoteId = validNoteId;
      const mutationPatientId = patientId;

      await cancelAttachmentQueries(mutationPatientId, mutationNoteId);

      // Snapshot for rollback
      const previousInfinite = queryClient.getQueriesData({
        queryKey: noteQueryKeys.attachmentsInfinite({
          patientId: mutationPatientId,
        }),
        exact: false,
      });
      const previousNoteAttachments = mutationNoteId
        ? queryClient.getQueryData<NoteAttachment[]>(
            noteQueryKeys.attachments({ noteId: mutationNoteId }),
          )
        : undefined;

      // Optimistically remove from caches
      removeAttachmentsFromCache(mutationPatientId, mutationNoteId, [
        attachmentId,
      ]);

      const previousSidebarCounts = optimisticAdjustSidebarCounts(
        mutationPatientId,
        { attachmentsCount: -1 },
      );

      return {
        previousSidebarCounts,
        attachmentId,
        previousInfinite,
        previousNoteAttachments,
        mutationNoteId,
        mutationPatientId,
      };
    },
    onSuccess: async (detached) => {
      detachChartDosesInPatientCharts(detached);
      toast({
        title: "Success",
        description: "Attachment deleted successfully",
        variant: "success",
      });
    },
    onError: (err, _vars, ctx) => {
      ctx?.previousInfinite?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      if (ctx?.mutationNoteId && ctx?.previousNoteAttachments !== undefined) {
        queryClient.setQueryData(
          noteQueryKeys.attachments({ noteId: ctx.mutationNoteId }),
          ctx.previousNoteAttachments,
        );
      }
      if (ctx?.mutationPatientId) {
        revertSidebarCounts(ctx.mutationPatientId, ctx?.previousSidebarCounts);
      }
      toast(
        isPermissionDeniedError(err)
          ? PHOTO_DELETE_PERMISSION_DENIED_TOAST
          : {
              title: "Error",
              description: "Failed to delete attachment",
              variant: "error",
            },
      );
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: async ({
      attachmentId,
      name,
    }: {
      attachmentId: string;
      name: string;
    }) => await updateNoteAttachmentName(attachmentId, name),
    onSuccess: (_, { attachmentId, name }) => {
      updateAttachmentsCache(null, null, (attachments) =>
        attachments.map((attachment) =>
          attachment.id === attachmentId
            ? { ...attachment, name, originalFilename: name }
            : attachment,
        ),
      );
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Failed to update attachment name. Please try again.",
        variant: "error",
      });
    },
  });

  const updateAnnotationsMutation = useAnnotationMutation(
    patientId || "",
    validNoteId || null,
  );

  const restoreAnnotationMutation = useRestoreAnnotationMutation(
    patientId || "",
    validNoteId || null,
  );

  const isLoading = [
    uploadAttachmentMutation.isPending,
    addFromUrlMutation.isPending,
    deleteAttachmentMutation.isPending,
    updateNameMutation.isPending,
    updateAnnotationsMutation.isPending,
    restoreAnnotationMutation.isPending,
  ].some((isLoading) => isLoading);

  return {
    isLoading,
    uploadAttachmentMutation,
    addFromUrlMutation,
    deleteAttachmentMutation,
    updateNameMutation,
    updateAnnotationsMutation,
    restoreAnnotationMutation,
  };
};

// Update the title of a note
export const useUpdateNoteTitleMutation = () =>
  useMutation<
    { id: string; title: string } | null,
    Error,
    { noteId: string; title: string }
  >({
    mutationFn: async ({ noteId, title }) =>
      await updateNoteTitle(noteId, title),
    onSuccess: (data) => {
      if (!data) return;
      // Update note detail cache
      queryClient.setQueryData<NoteDetail>(
        noteQueryKeys.detail(data.id),
        (prev) => (prev ? { ...prev, title: data.title } : prev),
      );
      // Update list caches
      const allLists = queryClient.getQueriesData<Page<NoteListItem>>({
        queryKey: noteQueryKeys.lists(),
        exact: false,
      });
      allLists.forEach(([key, page]) => {
        if (!page) return;
        const typedKey = key as readonly unknown[];
        queryClient.setQueryData<Page<NoteListItem>>(typedKey, {
          ...page,
          data: page.data.map((n) =>
            n.id === data.id ? { ...n, title: data.title } : n,
          ),
        });
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update title",
        variant: "error",
      });
    },
  });

// Persist the template selected for a note before generation (AI scribe).
// The note detail cache is patched separately with the full template object at the
// call site; this mutation only durably stores the selection so it survives navigation.
export const useUpdateNoteTemplateIdMutation = () =>
  useMutation<
    { id: string; templateId: string } | null,
    Error,
    { noteId: string; templateId: string }
  >({
    mutationFn: async ({ noteId, templateId }) =>
      await updateNoteTemplateId(noteId, templateId),
    onSuccess: (result) => {
      if (result) patchEventNoteTemplateInCaches(result.id, result.templateId);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save the selected template",
        variant: "error",
      });
    },
  });

export const useUpdateNoteEventLinkMutation = () =>
  useMutation<
    { id: string; eventId: string | null } | null,
    Error,
    {
      noteId: string;
      eventId: string | null;
      /** Appointment `start` when linking — keeps visit date in sync optimistically. */
      optimisticVisitDate?: string | null;
    },
    | {
        previousDetails: ReturnType<
          typeof queryClient.getQueriesData<NoteDetail>
        >;
        previousLists: ReturnType<
          typeof queryClient.getQueriesData<Page<NoteListItem>>
        >;
        parentNoteId: string;
      }
    | undefined
  >({
    mutationFn: async ({ noteId, eventId }) =>
      await updateNoteEventLink(noteId, eventId),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: noteQueryKeys.all });

      const previousDetails = queryClient.getQueriesData<NoteDetail>({
        queryKey: [...noteQueryKeys.all, "detail"],
        exact: false,
      });
      const previousLists = queryClient.getQueriesData<Page<NoteListItem>>({
        queryKey: noteQueryKeys.lists(),
        exact: false,
      });

      const anchor = queryClient.getQueryData<NoteDetail>(
        noteQueryKeys.detail(variables.noteId),
      );
      const parentNoteId = anchor?.parentNoteId ?? variables.noteId;
      const revs = queryClient.getQueryData<NoteSummary[]>(
        noteQueryKeys.revisions({ parentNoteId }),
      );
      const chainIds = new Set(revs?.map((r) => r.id) ?? [variables.noteId]);
      let firstChainCreated: string | undefined;
      if (revs?.length) {
        firstChainCreated = revs.reduce(
          (min, r) => (toMs(r.createdDate) < toMs(min) ? r.createdDate : min),
          revs[0]!.createdDate,
        );
      }

      queryClient.setQueriesData<NoteDetail>(
        { queryKey: [...noteQueryKeys.all, "detail"], exact: false },
        (prev) => {
          if (!prev || prev.parentNoteId !== parentNoteId) return prev;
          const nextVisit = variables.eventId
            ? (variables.optimisticVisitDate ??
              prev.visitDate ??
              prev.createdDate)
            : (firstChainCreated ?? prev.createdDate);
          return {
            ...prev,
            eventId: variables.eventId ?? undefined,
            visitDate: nextVisit,
          };
        },
      );

      queryClient.setQueriesData<Page<NoteListItem>>(
        { queryKey: noteQueryKeys.lists(), exact: false },
        (prev) => {
          if (!prev?.data) return prev;
          let changed = false;
          const data = prev.data.map((n) => {
            if (!chainIds.has(n.id)) return n;
            changed = true;
            const nextVisit = variables.eventId
              ? (variables.optimisticVisitDate ?? n.visitDate ?? n.createdDate)
              : (firstChainCreated ?? n.visitDate);
            return {
              ...n,
              eventId: variables.eventId ?? undefined,
              visitDate: nextVisit,
            };
          });
          return changed ? { ...prev, data } : prev;
        },
      );

      return { previousDetails, previousLists, parentNoteId };
    },
    onError: (_err, _variables, context) => {
      context?.previousDetails.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      context?.previousLists.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      toast({
        title: "Error",
        description: "Could not update the appointment link.",
        variant: "error",
      });
    },
    onSuccess: (data, _variables, context) => {
      if (!data) return;
      const cachedChain = getCachedNoteChainIds(data.id, context?.parentNoteId);
      patchEventNoteIdsInCaches({
        removeNoteIds: cachedChain.chainIds,
        addEventId: data.eventId ?? undefined,
        addNoteId: data.id,
      });
      // Linked from the notes list, the note has no cached detail to give the
      // appointment its author and template; refetch the appointment so its
      // checklist can match the note.
      if (
        data.eventId &&
        !queryClient.getQueryData<NoteDetail>(noteQueryKeys.detail(data.id))
      ) {
        void queryClient.invalidateQueries({
          queryKey: eventQueryKeys.detail(data.eventId),
        });
        void queryClient.invalidateQueries({
          queryKey: eventQueryKeys.allPatientResults(),
          exact: false,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: [...noteQueryKeys.all, "detail"],
        exact: false,
      });
      void queryClient.invalidateQueries({
        queryKey: [...noteQueryKeys.all, "revisions"],
        exact: false,
      });
      void queryClient.invalidateQueries({
        queryKey: noteQueryKeys.lists(),
        exact: false,
      });
      toast({
        title: "Saved",
        description: data.eventId
          ? "Appointment linked to this note."
          : "Appointment link removed.",
        variant: "success",
      });
    },
  });

export const useArchiveNoteMutation = () => {
  return useMutation<
    boolean | null,
    Error,
    { noteId?: string; noteTranscriptId?: string },
    | {
        previousLists: ReturnType<
          typeof queryClient.getQueriesData<Page<NoteListItem>>
        >;
        previousSidebarCounts?: PatientSidebarCounts;
      }
    | undefined
  >({
    mutationFn: async ({
      noteId,
      noteTranscriptId,
    }: {
      noteId?: string;
      noteTranscriptId?: string;
    }) => {
      const archived = await archiveNote(noteId, noteTranscriptId);
      if (!archived) throw new Error("Failed to delete note");
      return archived;
    },
    onMutate: async (variables) => {
      // Optimistically remove the note from all list queries where it exists
      await queryClient.cancelQueries({ queryKey: noteQueryKeys.lists() });

      const previousLists = queryClient.getQueriesData<Page<NoteListItem>>({
        queryKey: noteQueryKeys.lists(),
        exact: false,
      });

      const noteId = variables.noteId;
      let previousSidebarCounts: PatientSidebarCounts | undefined;
      if (noteId) {
        const detail = queryClient.getQueryData<NoteDetail | undefined>(
          noteQueryKeys.detail(noteId),
        );
        const pid = detail?.patient?.id;
        if (pid) {
          previousSidebarCounts = optimisticAdjustSidebarCounts(pid, {
            notesCount: -1,
          });
        }
      }

      const chainIds = noteId
        ? getCachedNoteChainIds(noteId).chainIds
        : new Set<string>();
      previousLists.forEach(([key, page]) => {
        if (!page || !noteId) return;
        const beforeLength = page.data.length;
        const newData = page.data.filter((n) => !chainIds.has(n.id));
        if (newData.length !== beforeLength) {
          const typedKey = key as readonly unknown[];
          queryClient.setQueryData<Page<NoteListItem>>(typedKey, {
            ...page,
            data: newData,
            total: Math.max(0, page.total - (beforeLength - newData.length)),
          });
        }
      });

      return { previousLists, previousSidebarCounts };
    },
    onError: (_err, _vars, context) => {
      context?.previousLists?.forEach(([key, page]) => {
        if (page) {
          const typedKey = key as readonly unknown[];
          queryClient.setQueryData(typedKey, page);
        }
      });
      // If we had a patientId for the archived note, revert by reconstructing patientId from cached detail again
      const noteId = _vars?.noteId;
      if (noteId) {
        const detail = queryClient.getQueryData<NoteDetail | undefined>(
          noteQueryKeys.detail(noteId),
        );
        const pid = detail?.patient?.id;
        if (pid) revertSidebarCounts(pid, context?.previousSidebarCounts);
      }
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "error",
      });
    },
    onSuccess: async (_data, variables) => {
      if (variables.noteId) {
        const { chainIds } = getCachedNoteChainIds(variables.noteId);
        patchEventNoteIdsInCaches({ removeNoteIds: chainIds });
        // Forward navigation must not render a fresh cached deleted revision.
        // Cancel pending reads first so they cannot repopulate an evicted detail.
        await Promise.all(
          [...chainIds].map((id) =>
            queryClient.cancelQueries({ queryKey: noteQueryKeys.detail(id) }),
          ),
        );
        chainIds.forEach((id) => {
          queryClient.removeQueries({ queryKey: noteQueryKeys.detail(id) });
        });
      }
      // The deleted note may be the one a new note would fill in from.
      invalidatePrefilledAnswers();
      toast({
        title: "Success",
        description: "Note deleted successfully",
        variant: "success",
      });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.counts() });
      if (variables?.noteId) {
        queryClient.invalidateQueries({
          queryKey: noteQueryKeys.detail(variables.noteId),
        });
      }
    },
  });
};

// Toggle pin/unpin on a note
export const useTogglePinNoteMutation = () =>
  useMutation<
    { id: string; isPinned: boolean } | null,
    Error,
    { noteId: string }
  >({
    mutationFn: async ({ noteId }) => await togglePinNote(noteId),
    onSuccess: (data) => {
      if (data) {
        // Optimistically update the note in all list caches
        const allLists = queryClient.getQueriesData<Page<NoteListItem>>({
          queryKey: noteQueryKeys.lists(),
          exact: false,
        });
        allLists.forEach(([key, page]) => {
          if (!page) return;
          const typedKey = key as readonly unknown[];
          queryClient.setQueryData<Page<NoteListItem>>(typedKey, {
            ...page,
            data: page.data.map((n) =>
              n.id === data.id ? { ...n, isPinned: data.isPinned } : n,
            ),
          });
        });

        toast({
          title: data.isPinned ? "Note pinned" : "Note unpinned",
          variant: "success",
        });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update pin status",
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });
    },
  });

/** Mark a note finished, or put it back to a draft. */
export const useSetNoteCompletionMutation = () =>
  useMutation<
    NoteCompletionResult | null,
    Error,
    { noteId: string; isComplete: boolean }
  >({
    mutationFn: async ({ noteId, isComplete }) =>
      await setNoteCompletion(noteId, isComplete),
    onSuccess: (data) => {
      if (!data) return;
      const patch = {
        completedDate: data.completedDate ?? undefined,
        completedByProviderId: data.completedByProviderId ?? undefined,
      };
      queryClient.setQueriesData<Page<NoteListItem>>(
        { queryKey: noteQueryKeys.lists(), exact: false },
        (page) => {
          if (!page?.data) return page;
          return {
            ...page,
            data: page.data.map((n) =>
              n.id === data.id ? { ...n, ...patch } : n,
            ),
          };
        },
      );
      queryClient.setQueryData<NoteDetail>(
        noteQueryKeys.detail(data.id),
        (prev) => (prev ? { ...prev, ...patch } : prev),
      );
      toast({
        title: data.completedDate
          ? "Note marked complete"
          : "Note reopened as a draft",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update the note's status",
        variant: "error",
      });
    },
  });

// Create/update a note (saves a new revision or creates a new note)
export const useCreateNoteMutation = () =>
  useMutation<
    NoteDetail | null,
    Error,
    NoteCreate,
    { previousSidebarCounts?: PatientSidebarCounts; patientId?: string }
  >({
    mutationFn: async (payload) => await createNote(payload),
    onMutate: async (payload) => {
      const pid = payload.patientId;
      if (!pid) return {};
      const previousSidebarCounts = optimisticAdjustSidebarCounts(pid, {
        notesCount: 1,
      });
      return { previousSidebarCounts, patientId: pid };
    },
    onSuccess: (result, _vars, ctx) => {
      if (!result) {
        if (ctx?.patientId)
          revertSidebarCounts(ctx.patientId, ctx?.previousSidebarCounts);
        return;
      }
      // Optimistically set the new note detail content if we have it
      queryClient.setQueryData<NoteDetail>(
        noteQueryKeys.detail(result.id),
        (prev) => (prev ? { ...prev, ...result } : result),
      );
      syncEventNoteIdsFromChain(result);

      // Invalidate revisions for this parent so version list refreshes
      if (result.parentNoteId) {
        queryClient.invalidateQueries({
          queryKey: noteQueryKeys.revisions({
            parentNoteId: result.parentNoteId,
          }),
        });
      }

      // Ensure list pages and counts reflect the change
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.counts() });

      // Manual note creation can process mappings, but this endpoint currently
      // returns a lightweight patient with measurements/medications set to null.
      // Keep the patient refetch here; only /notes/scribe returns seeded fields.
      if (result.patient?.id) {
        queryClient.invalidateQueries({
          queryKey: patientQueryKeys.detail(result.patient.id),
        });
        // Sold-items answers and note-mapped medications feed the checkout
        // "Today's Items" panel for this patient.
        invalidateNoteDoseCaches(result.patient.id);
      }

      // Invalidate prefilled answers since note mappings may have updated dynamic fields
      invalidatePrefilledAnswers();
    },
    onError: async (err, _vars, ctx) => {
      if (ctx?.patientId)
        revertSidebarCounts(ctx.patientId, ctx?.previousSidebarCounts);
      // The editor asks the provider how to settle this one.
      if ((await parseErrorCode(err)) === "note_save_conflict") return;
      const errorMessage = await parseError(err, "Failed to save note");
      toast({
        title: "Error",
        description: errorMessage,
        variant: "error",
      });
    },
  });

// Generate AI note from transcript
export const useGenerateAiNoteMutation = () =>
  useMutation<
    NoteDetail | null,
    Error,
    {
      patientId: string;
      providerId: string;
      noteId: string;
      templateId?: string;
    }
  >({
    mutationFn: async (vars) => {
      // Kick off background audio combine (don't block)
      void combineAudioFiles(vars.noteId);
      return await createAiNote(
        vars.patientId,
        vars.providerId,
        vars.noteId,
        vars.templateId,
      );
    },
    onSuccess: (response, variables) => {
      if (!response) return;
      // mappingSkips describes this generation attempt, not the note. Merging it
      // into the cached detail would keep the notice alive for every later render
      // of a note the server reports nothing about. Callers read it off the
      // mutation result instead.
      const { mappingSkips, ...noteDetail } = response;
      queryClient.setQueryData<NoteDetail>(
        noteQueryKeys.detail(response.id),
        (prev) => (prev ? { ...prev, ...noteDetail } : noteDetail),
      );
      // Held under its own key so it survives the remount when regeneration
      // navigates to the new revision, and dies with the cache on a real reload.
      queryClient.setQueryData(
        noteQueryKeys.mappingSkips(response.id),
        mappingSkips ?? null,
      );
      syncEventNoteIdsFromChain(response);
      // Also ensure the lists reflect updated summary if needed
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });

      // Seed patient caches with the timezone-scoped patient returned by the
      // note-detail response instead of refetching the whole patient.
      seedPatientCachesFromNotePatient(response.patient);
      if (variables.patientId) {
        // Note-mapped medications/sold-items feed the checkout "Today's Items".
        invalidateNoteDoseCaches(variables.patientId);
      }

      // Invalidate prefilled answers since note mappings may have updated dynamic fields
      invalidatePrefilledAnswers();
    },
  });

// Chat with AI to modify a note (creates a new revision)
export const useChatWithAiNoteMutation = () =>
  useMutation<
    NoteScribeResponse | null,
    Error,
    {
      parentNoteId: string;
      providerId: string;
      message: string;
      patientId?: string;
    }
  >({
    mutationFn: async (vars) =>
      await chatWithAiNote(vars.parentNoteId, vars.providerId, vars.message),
    onSuccess: (response, variables) => {
      if (!response) return;
      syncEventNoteIdsFromChain({
        id: response.id,
        parentNoteId: variables.parentNoteId,
        createdDate: response.createdDate,
        // The chat revision is recorded under whoever asked for the change.
        noteRef: {
          providerId: variables.providerId,
          templateId: response.template?.id,
        },
      });
      // New revision created - refresh revision list and note details
      queryClient.invalidateQueries({
        queryKey: noteQueryKeys.revisions({
          parentNoteId: variables.parentNoteId,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: [...noteQueryKeys.all, "detail"],
      });
      // Ensure list tables reflect new activity
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });
      // Invalidate patient query to refresh medications, measurements, etc.
      // that may have been updated via note mappings
      if (variables.patientId) {
        queryClient.invalidateQueries({
          queryKey: patientQueryKeys.detail(variables.patientId),
        });
        // Note-mapped medications/sold-items feed the checkout "Today's Items".
        invalidateNoteDoseCaches(variables.patientId);
      }

      // Invalidate prefilled answers since note mappings may have updated dynamic fields
      invalidatePrefilledAnswers();
    },
  });

export const useSignOffNoteMutation = () => {
  const { currentUser } = useUser();
  return useMutation<
    NoteDetail | null,
    Error,
    { noteId: string },
    { previousNote: NoteDetail | undefined }
  >({
    mutationFn: async ({ noteId }) => {
      return await signOffNote(noteId);
    },
    onMutate: async ({ noteId }) => {
      await queryClient.cancelQueries({
        queryKey: noteQueryKeys.detail(noteId),
      });
      const previousNote = queryClient.getQueryData<NoteDetail>(
        noteQueryKeys.detail(noteId),
      );
      if (previousNote) {
        const now = new Date().toISOString();
        // A note that is already signed takes the caller's signature as its
        // co-signature; the first signer must stay on screen.
        queryClient.setQueryData<NoteDetail>(
          noteQueryKeys.detail(noteId),
          previousNote.signedOffProviderId
            ? {
                ...previousNote,
                coSignedProviderId: currentUser?.id,
                coSignedDate: now,
                requiresCoSign: false,
              }
            : {
                ...previousNote,
                signedOffProviderId: currentUser?.id,
                signedOffDate: now,
                // Signing completes the note server-side; mirror that here.
                completedDate: previousNote.completedDate ?? now,
              },
        );
      }
      return { previousNote };
    },
    onSuccess: (result) => {
      if (!result) return;
      queryClient.setQueryData<NoteDetail>(
        noteQueryKeys.detail(result.id),
        (prev) => (prev ? { ...prev, ...result } : result),
      );
      patchSignedOffNoteInListCaches(result);
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });
      patchSignedOffNoteInRevisionCaches(result);
      // No success toast: every sign-off surface swaps the button for the
      // "Signed off" badge, so the toast only covers the note header.
    },
    onError: (err, _vars, context) => {
      if (context?.previousNote) {
        queryClient.setQueryData(
          noteQueryKeys.detail(context.previousNote.id),
          context.previousNote,
        );
      }
      // The server says why a signature was refused (a template that wants the
      // author or a role to sign); show that rather than a bare failure.
      void parseError(err, "Failed to sign off note").then((description) =>
        toast({ title: "Couldn't sign off", description, variant: "error" }),
      );
    },
  });
};

export const useRequestNoteSignOffMutation = () =>
  useMutation<
    NoteDetail | null,
    Error,
    { noteId: string; providerId: string; message?: string }
  >({
    mutationFn: async ({ noteId, providerId, message }) =>
      await requestNoteSignOff(noteId, providerId, message),
    onSuccess: (result) => {
      if (!result) return;
      queryClient.setQueryData<NoteDetail>(
        noteQueryKeys.detail(result.id),
        (prev) => (prev ? { ...prev, ...result } : result),
      );
      queryClient.setQueriesData<Page<NoteListItem>>(
        { queryKey: noteQueryKeys.lists(), exact: false },
        (oldData) => {
          if (!oldData?.data.some((item) => item.id === result.id))
            return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === result.id
                ? {
                    ...item,
                    pendingSignOffRequests: result.pendingSignOffRequests,
                  }
                : item,
            ),
          };
        },
      );
      const assignee = result.pendingSignOffRequests?.at(-1)?.assigneeName;
      toast({
        title: "Sign-off requested",
        description: assignee ? `Sent to ${assignee}` : undefined,
        variant: "success",
      });
    },
    onError: (err) => {
      void parseError(err, "Failed to request sign-off").then((description) =>
        toast({
          title: "Couldn't request sign-off",
          description,
          variant: "error",
        }),
      );
    },
  });

export const useBulkSignOffNotesMutation = () =>
  useMutation<NoteBulkSignOffResult | null, Error, NoteBulkSignOffRequest>({
    mutationFn: async (request) => await bulkSignOffNotes(request),
    onSuccess: (result) => {
      // A dry run changes nothing. A real run is reported on the list itself
      // (who was signed, who was skipped and why), so there is no toast. Lists,
      // queue counts and open details are refetched rather than patched: the
      // response carries ids, not notes.
      if (result?.dryRun) return;
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.all });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to sign off notes",
        variant: "error",
      });
    },
  });

// Upload note from image/PDF
export const useUploadNoteMutation = () =>
  useMutation<
    NoteScribeResponse | null,
    Error,
    {
      files: File[];
      patientId: string;
      providerId: string;
      templateId?: string;
      locationId?: string;
      skipAiGeneration?: boolean;
    },
    { previousSidebarCounts?: PatientSidebarCounts; patientId?: string }
  >({
    mutationFn: async (vars) => {
      const formData = new FormData();
      // Append all files
      vars.files.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("patient_id", vars.patientId);
      formData.append("provider_id", vars.providerId);
      if (vars.templateId) {
        formData.append("template_id", vars.templateId);
      }
      if (vars.locationId) {
        formData.append("location_id", vars.locationId);
      }
      if (vars.skipAiGeneration !== undefined) {
        formData.append("skip_ai_generation", String(vars.skipAiGeneration));
      }

      return await requestData<NoteScribeResponse>(
        "notes/upload",
        "post",
        {
          body: formData,
          timeout: 60 * 1000 * 5,
        },
        true,
      );
    },
    onMutate: async (vars) => {
      const pid = vars.patientId;
      const deltas: Partial<PatientSidebarCounts> = { notesCount: 1 };
      if (Array.isArray(vars.files) && vars.files.length > 0) {
        deltas.attachmentsCount = vars.files.length;
      }
      const previousSidebarCounts = optimisticAdjustSidebarCounts(pid, deltas);
      return { previousSidebarCounts, patientId: pid };
    },
    onSuccess: (response, variables) => {
      if (!response) return;
      // Invalidate note detail to refresh with new answers
      queryClient.invalidateQueries({
        queryKey: noteQueryKeys.detail(response.id),
      });
      // Also ensure the lists reflect updated summary if needed
      queryClient.invalidateQueries({ queryKey: noteQueryKeys.lists() });

      // Invalidate patient query to refresh medications, measurements, etc.
      // that may have been updated via note mappings
      if (variables.patientId) {
        queryClient.invalidateQueries({
          queryKey: patientQueryKeys.detail(variables.patientId),
        });
        // Note-mapped medications/sold-items feed the checkout "Today's Items".
        invalidateNoteDoseCaches(variables.patientId);
      }

      // Invalidate prefilled answers since note mappings may have updated dynamic fields
      invalidatePrefilledAnswers();
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.patientId)
        revertSidebarCounts(ctx.patientId, ctx?.previousSidebarCounts);
    },
  });

// Non-hook actions for use outside React components
export const startRecordingSessionAction = async (
  patientId: string,
  locationId?: string,
  overrideOptOut?: boolean,
  eventId?: string | null,
  noteTemplateId?: string | null,
) => {
  const result = await startRecordingSession(
    patientId,
    locationId,
    overrideOptOut,
    eventId,
    noteTemplateId,
  );
  if (result) {
    queryClient.setQueryData<NoteDetail>(
      noteQueryKeys.detail(result.id),
      result,
    );
    syncEventNoteIdsFromChain(result);
  }
  // Make sure lists are refreshed so in-progress note appears
  queryClient.invalidateQueries({ queryKey: noteQueryKeys.list({}) });
  return result;
};

export const combineAudioFilesAction = async (noteTranscriptId: string) => {
  return await combineAudioFiles(noteTranscriptId);
};

export const useTagMutations = () => {
  const createTagMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color?: string }) =>
      await createNoteAttachmentTag(name, color),
    onSuccess: (data) => {
      if (!data) return;
      updateTagsCache((tags) => {
        const byId = new Map(tags.map((tag) => [tag.id, tag]));
        byId.set(data.id, data);
        return Array.from(byId.values());
      });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: async ({
      tagId,
      data,
    }: {
      tagId: string;
      data: { name?: string; color?: string };
    }) => await updateNoteAttachmentTag(tagId, data),
    onSuccess: (data) => {
      if (!data) return;
      updateTagsCache((tags) =>
        tags.map((tag) => (tag.id === data.id ? data : tag)),
      );
      updateEmbeddedAttachmentTags((tags) =>
        tags.map((tag) => (tag.id === data.id ? data : tag)),
      );
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: string) => await deleteNoteAttachmentTag(tagId),
    onSuccess: (_data, tagId) => {
      updateTagsCache((tags) => tags.filter((tag) => tag.id !== tagId));
      updateEmbeddedAttachmentTags((tags) =>
        tags.filter((tag) => tag.id !== tagId),
      );
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          const params = queryKey[3];
          return (
            queryKey[0] === noteQueryKeys.all[0] &&
            queryKey[1] === noteQueryKeys.all[1] &&
            queryKey[2] === "attachments-infinite" &&
            typeof params === "object" &&
            params !== null &&
            Array.isArray((params as { tagIds?: string[] }).tagIds) &&
            (params as { tagIds: string[] }).tagIds.includes(tagId)
          );
        },
      });
    },
  });

  const assignTagsMutation = useMutation<
    { message: string } | null,
    Error,
    { attachmentId: string; tagIds: string[] },
    {
      previousInfiniteQueries: Array<{ queryKey: any; data: any }>;
      previousNoteAttachments: Array<{ queryKey: any; data: any }>;
    }
  >({
    mutationFn: async ({
      attachmentId,
      tagIds,
    }: {
      attachmentId: string;
      tagIds: string[];
    }) => await assignNoteAttachmentTags(attachmentId, tagIds),
    onMutate: async ({ attachmentId, tagIds }) => {
      // Cancel outgoing queries
      await cancelAttachmentQueries(null, null);

      // Get all tags to map IDs to tag objects
      const tagsQuery = queryClient.getQueryData<
        { data: NoteAttachmentTag[] } | NoteAttachmentTag[]
      >(noteQueryKeys.tags());
      const allTags = Array.isArray(tagsQuery)
        ? tagsQuery
        : (tagsQuery?.data ?? []);
      const tagMap = new Map(allTags.map((tag) => [tag.id, tag]));
      const newTags = tagIds
        .map((id) => tagMap.get(id))
        .filter(Boolean) as NoteAttachmentTag[];

      // Get all matching queries before updating (for rollback)
      const infiniteQueries = queryClient.getQueriesData({
        queryKey: [...noteQueryKeys.all, "attachments-infinite"],
        exact: false,
      });
      const noteAttachmentQueries = queryClient.getQueriesData({
        queryKey: [...noteQueryKeys.all, "attachments"],
        exact: false,
      });

      // Store previous data for rollback
      const previousInfiniteQueries = infiniteQueries.map(
        ([queryKey, data]) => ({
          queryKey,
          data,
        }),
      );
      const previousNoteAttachments = noteAttachmentQueries.map(
        ([queryKey, data]) => ({
          queryKey,
          data,
        }),
      );

      // Optimistically update caches
      updateAttachmentsCache(null, null, (data) =>
        data.map((attachment: NoteAttachment) =>
          attachment.id === attachmentId
            ? { ...attachment, tags: newTags }
            : attachment,
        ),
      );

      return { previousInfiniteQueries, previousNoteAttachments };
    },
    onError: (_err, _vars, context) => {
      // Revert optimistic updates
      context?.previousInfiniteQueries.forEach(({ queryKey, data }) => {
        if (data && queryKey) {
          queryClient.setQueryData(queryKey, data);
        }
      });

      context?.previousNoteAttachments.forEach(({ queryKey, data }) => {
        if (data && queryKey) {
          queryClient.setQueryData(queryKey, data);
        }
      });

      toast({
        title: "Error",
        description: "Failed to update tags. Please try again.",
        variant: "error",
      });
    },
  });

  const bulkAssignTagsMutation = useMutation<
    { message: string } | null,
    Error,
    { attachmentIds: string[]; tagIds: string[] },
    {
      previousInfiniteQueries: Array<{ queryKey: any; data: any }>;
      previousNoteAttachments: Array<{ queryKey: any; data: any }>;
    }
  >({
    mutationFn: async ({
      attachmentIds,
      tagIds,
    }: {
      attachmentIds: string[];
      tagIds: string[];
    }) => await bulkAssignNoteAttachmentTags(attachmentIds, tagIds),
    onMutate: async ({ attachmentIds, tagIds }) => {
      await cancelAttachmentQueries(null, null);

      // Get all tags to map IDs to tag objects
      const tagsQuery = queryClient.getQueryData<
        { data: NoteAttachmentTag[] } | NoteAttachmentTag[]
      >(noteQueryKeys.tags());
      const allTags = Array.isArray(tagsQuery)
        ? tagsQuery
        : (tagsQuery?.data ?? []);
      const tagMap = new Map(allTags.map((tag) => [tag.id, tag]));
      const tagsToAdd = tagIds
        .map((id) => tagMap.get(id))
        .filter(Boolean) as NoteAttachmentTag[];

      const attachmentIdSet = new Set(attachmentIds);

      // Store previous data for rollback
      const infiniteQueries = queryClient.getQueriesData({
        queryKey: [...noteQueryKeys.all, "attachments-infinite"],
        exact: false,
      });
      const noteAttachmentQueries = queryClient.getQueriesData({
        queryKey: [...noteQueryKeys.all, "attachments"],
        exact: false,
      });

      const previousInfiniteQueries = infiniteQueries.map(
        ([queryKey, data]) => ({ queryKey, data }),
      );
      const previousNoteAttachments = noteAttachmentQueries.map(
        ([queryKey, data]) => ({ queryKey, data }),
      );

      // Optimistically add tags to matching attachments
      updateAttachmentsCache(null, null, (data) =>
        data.map((attachment: NoteAttachment) => {
          if (!attachmentIdSet.has(attachment.id)) return attachment;
          const existingTagIds = new Set(
            (attachment.tags ?? []).map((t) => t.id),
          );
          const newTags = [
            ...(attachment.tags ?? []),
            ...tagsToAdd.filter((t) => !existingTagIds.has(t.id)),
          ];
          return { ...attachment, tags: newTags };
        }),
      );

      return { previousInfiniteQueries, previousNoteAttachments };
    },
    onError: (_err, _vars, context) => {
      context?.previousInfiniteQueries.forEach(({ queryKey, data }) => {
        if (data && queryKey) queryClient.setQueryData(queryKey, data);
      });
      context?.previousNoteAttachments.forEach(({ queryKey, data }) => {
        if (data && queryKey) queryClient.setQueryData(queryKey, data);
      });

      toast({
        title: "Error",
        description: "Failed to assign tags. Please try again.",
        variant: "error",
      });
    },
  });

  return {
    createTagMutation,
    updateTagMutation,
    deleteTagMutation,
    assignTagsMutation,
    bulkAssignTagsMutation,
  };
};

export const useUploadNoteDocumentMutation = (noteId: string) => {
  return useMutation({
    mutationFn: async ({
      file,
      title,
      docType,
    }: {
      file: File;
      title: string;
      docType?: string;
    }) => await uploadNoteDocument(noteId, file, title, docType),
    onSuccess: (data) => {
      if (data) mergeDocumentsIntoNoteCache(noteId, [data]);
      toast({
        title: "Success",
        description: "Document uploaded successfully",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to upload document",
        variant: "error",
      });
    },
  });
};

export const useLinkDocumentsToNoteMutation = (noteId: string) => {
  return useMutation({
    mutationFn: async (documentIds: string[]) =>
      await linkDocumentsToNote(noteId, documentIds),
    onSuccess: (data) => {
      if (data?.length) {
        queryClient.setQueryData<DocumentSummary[] | null>(
          noteQueryKeys.noteDocuments(noteId),
          (oldData) => {
            if (!oldData) return sortDocumentsByCreatedDateDesc(data);
            const byId = new Map(oldData.map((doc) => [doc.id, doc]));
            data.forEach((doc) => byId.set(doc.id, doc));
            return sortDocumentsByCreatedDateDesc(Array.from(byId.values()));
          },
        );
      }
      toast({
        title: "Success",
        description: "Documents linked to note",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to link documents",
        variant: "error",
      });
    },
  });
};

export const useUnlinkDocumentFromNoteMutation = (noteId: string) => {
  return useMutation({
    mutationFn: async (documentId: string) =>
      await unlinkDocumentFromNote(noteId, documentId),
    onSuccess: (_data, documentId) => {
      queryClient.setQueryData<DocumentSummary[] | null>(
        noteQueryKeys.noteDocuments(noteId),
        (oldData) => oldData?.filter((doc) => doc.id !== documentId) ?? oldData,
      );
      toast({
        title: "Success",
        description: "Document removed from note",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove document",
        variant: "error",
      });
    },
  });
};

export const useChangeAttachmentPatientMutation = (
  currentPatientId: string,
) => {
  return useMutation<
    DetachedChartDoses | null,
    Error,
    { attachmentId: string; newPatientId: string }
  >({
    mutationFn: async ({ attachmentId, newPatientId }) => {
      return await changeAttachmentPatient(attachmentId, newPatientId);
    },
    onSuccess: (detached) => {
      detachChartDosesInPatientCharts(detached);
      // Invalidate all attachment queries to refresh grid
      queryClient.invalidateQueries({
        queryKey: noteQueryKeys.all,
      });
      // Also invalidate the old patient's sidebar counts
      if (currentPatientId) {
        queryClient.invalidateQueries({
          queryKey: patientQueryKeys.sidebarCounts(currentPatientId),
        });
      }
      toast({
        title: "Success",
        description: "Patient changed successfully",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to change patient. Please try again.",
        variant: "error",
      });
    },
  });
};

export const useUpdateNoteTemplatePatientViewMutation = () =>
  useMutation<
    Awaited<ReturnType<typeof updateNoteTemplatePatientView>>,
    Error,
    {
      noteTemplateId: string;
      visibleToPatient: boolean;
      patientVisibleQuestionOrder: string[] | null;
      showSummaryInPatientView: boolean;
    }
  >({
    mutationFn: async ({
      noteTemplateId,
      visibleToPatient,
      patientVisibleQuestionOrder,
      showSummaryInPatientView,
    }) =>
      await updateNoteTemplatePatientView(noteTemplateId, {
        visibleToPatient,
        patientVisibleQuestionOrder,
        showSummaryInPatientView,
      }),
    onSuccess: (data) => {
      if (!data) {
        toast({
          title: "Error",
          description: "Failed to update patient view settings",
          variant: "error",
        });
        return;
      }
      patchTemplatePatientViewCaches(data);
      toast({
        title: "Success",
        description: "Patient view settings saved",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update patient view settings",
        variant: "error",
      });
    },
  });
